import React, { useState, useEffect } from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet"
import { Avatar, AvatarImage, AvatarFallback } from "../../../components/ui/Avatar"
import { Badge } from "../../../components/ui/badge"
import Icon from "../../../components/AppIcon"
import { listUserCertificates } from "../../../utils/certHelpers"

const TABS = [
  "Profile",
  "Learning",
  "Courses",
  "Programs",
  "Assessments",
  "Projects",
  "Skills",
  "Credentials",
  "Proof of Learning",
  "Activity",
  "Payments",
]

// Tabs with real data wired up. Everything else in TABS renders the shared
// "coming soon" placeholder below rather than fabricated numbers, since
// there's no programs/assessments/projects/skills/payments data source yet.
const LIVE_TABS = new Set(["Profile", "Learning", "Courses", "Credentials"])

const initials = (name, email) => {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/)
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?"
  }
  return (email?.[0] || "?").toUpperCase()
}

const formatDate = (ms) => (ms ? new Date(ms).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—")

const STATUS_BADGE = {
  active: { variant: "success", label: "Active" },
  "at-risk": { variant: "warning", label: "At Risk" },
  inactive: { variant: "secondary", label: "Inactive" },
}

const ComingSoon = ({ label }) => (
  <div className="py-16 text-center text-muted-foreground text-sm">
    <Icon name="Clock" size={24} className="mx-auto mb-2 opacity-50" />
    {label} isn't wired up yet.
  </div>
)

const LearnerProfileDrawer = ({ learner, organization, open, onClose }) => {
  const [activeTab, setActiveTab] = useState("Profile")
  const [certificates, setCertificates] = useState([])
  const [loadingCerts, setLoadingCerts] = useState(false)

  useEffect(() => {
    if (!open || !learner?.userId) return
    setActiveTab("Profile")
    setLoadingCerts(true)
    listUserCertificates(learner.userId).then(({ certificates: list }) => {
      setCertificates(list?.filter((c) => c?.orgId === organization?.orgId) || [])
      setLoadingCerts(false)
    })
  }, [open, learner?.userId, organization?.orgId])

  if (!learner) return null
  const badge = STATUS_BADGE[learner.status] || STATUS_BADGE.inactive

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Learner 360</SheetTitle>
        </SheetHeader>

        <div className="px-4 flex items-center gap-3 border-b border-border pb-4">
          <Avatar className="h-14 w-14">
            <AvatarImage src={learner.avatarUrl} alt={learner.name || learner.email} />
            <AvatarFallback className="text-lg">{initials(learner.name, learner.email)}</AvatarFallback>
          </Avatar>
                                        <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-foreground truncate">
              {learner.name || "Unnamed Learner"}
            </p>
            <p className="text-sm text-muted-foreground truncate">{learner.email || learner.userId}</p>
            {learner.nesolearnId && <p className="text-xs text-muted-foreground font-mono">{learner.nesolearnId}</p>}
          </div>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>

        <div className="px-4 flex gap-1 overflow-x-auto border-b border-border pb-2 [scrollbar-width:none]">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="px-4 pb-6 overflow-y-auto flex-1">
          {!LIVE_TABS.has(activeTab) && <ComingSoon label={activeTab} />}

          {activeTab === "Profile" && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Enrolled Since</p>
                  <p className="text-sm text-foreground">{formatDate(learner.enrolledAtMs)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Last Active</p>
                  <p className="text-sm text-foreground">{formatDate(learner.lastActivityMs)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Courses Enrolled</p>
                  <p className="text-sm text-foreground">{learner.coursesCount}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Average Progress</p>
                  <p className="text-sm text-foreground">{learner.avgProgress}%</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "Learning" && (
            <div className="space-y-3 pt-2">
              {learner.enrollments?.map((e) => (
                <div key={e.enrollmentId} className="p-3 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-foreground">
                      {learner.courseTitles?.[learner.enrollments.indexOf(e)] || "Untitled Course"}
                    </span>
                    <span className="text-xs text-muted-foreground">{e.progressPercent || 0}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${e.progressPercent || 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Courses" && (
            <div className="divide-y divide-border pt-2">
              {learner.enrollments?.map((e, i) => (
                <div key={e.enrollmentId} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{learner.courseTitles?.[i] || "Untitled Course"}</p>
                    <p className="text-xs text-muted-foreground">
                      {e.lessonsCompleted || 0}/{e.lessonsTotal || 0} lessons · {e.cohortName || "No cohort"}
                    </p>
                  </div>
                  <Badge variant={(e.progressPercent || 0) >= 100 ? "success" : "secondary"}>
                    {(e.progressPercent || 0) >= 100 ? "Completed" : "In Progress"}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Credentials" && (
            <div className="space-y-3 pt-2">
              {loadingCerts ? (
                <p className="text-sm text-muted-foreground">Loading credentials...</p>
              ) : certificates?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No credentials issued yet.</p>
              ) : (
                certificates.map((cert) => (
                  <div key={cert.certId} className="p-3 rounded-lg border border-border flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{cert.courseTitle}</p>
                      <p className="text-xs text-muted-foreground">Issued {cert.issueDate?.slice(0, 10)}</p>
                    </div>
                    <Badge variant={cert.status === "valid" ? "success" : "secondary"}>{cert.status}</Badge>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default LearnerProfileDrawer